import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_URL, APP_CONFIG, ConfigSettings } from '@shared/config';
import { Kpi } from '@shared/models';
import { Dashboard } from '@shared/models/dashboard.model';
import { KpiBoxService } from '@shared/services/kpi-box.service';
import { Observable, ReplaySubject } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';
import { HttpErrorSkip } from '../../interceptors/http-error/http-error-skip.model';
import { IsValidKpi } from '../models/kpi.model';
import { UrlParameter } from './url-parameters-getter/url-parameters-getter.config';
import { UrlParametersGetterService } from './url-parameters-getter/url-parameters-getter.service';

export interface KpiContext {
  kpi: Kpi | null;
  dashboard: Dashboard;
}

@Injectable()
export class KpiService {
  globalSettings: ConfigSettings;
  activeKpi: Kpi;

  private _activeKpiContext$ = new ReplaySubject<KpiContext | null>(1);
  activeKpiContext$: Observable<KpiContext | null> =
    this._activeKpiContext$.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) settings: ConfigSettings,
    private kpiBoxService: KpiBoxService,
    private urlParametersGetterService: UrlParametersGetterService
  ) {
    this.globalSettings = settings;
  }

  setActiveKpiContext(context: KpiContext): void {
    this.activeKpi = context.kpi ?? undefined;
    this._activeKpiContext$.next(context);
  }

  clearActiveKpiContext(): void {
    this.activeKpi = undefined;
    this._activeKpiContext$.next(null);
  }

  getKpiById(id: number, skip404: boolean): Observable<Kpi> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.kpi}/${id}`;
    if (!skip404) {
      return this.http.get<Kpi>(url, { withCredentials: true });
    }
    return this.http.get<Kpi>(url, {
      withCredentials: true,
      headers: {
        [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
          skipStatusCodes: [404],
        }).parseToString(),
      },
    });
  }

  isValidKpi(
    id: number,
    dashboard: string,
    skip404: boolean
  ): Observable<IsValidKpi> {
    const url = `${this.globalSettings.orApiUrl}${API_URL.kpi}/${id}/${dashboard}/isValid`;
    if (!skip404) {
      return this.http.get<IsValidKpi>(url, { withCredentials: true });
    }
    return this.http.get<IsValidKpi>(url, {
      withCredentials: true,
      headers: {
        [HttpErrorSkip.HEADER_KEY]: new HttpErrorSkip({
          skipStatusCodes: [404],
        }).parseToString(),
      },
    });
  }

  public nextKpi() {
    this.moveToKpi();
  }

  public previousKpi() {
    this.moveToKpi(-1);
  }

  private moveToKpi(offset: number = 1) {
    this.kpiBoxService.kpis$
      .pipe(
        first(),
        withLatestFrom(
          this.urlParametersGetterService.getParameter$(UrlParameter.KpiId)
        ),
        map(([kpis, selectedKpiId]) => ({
          kpis,
          selectedKpiId,
        }))
      )
      .subscribe({
        next: ({ kpis, selectedKpiId }) => {
          const kpiToSelect = this.getNextKpi(kpis, selectedKpiId, offset);
          this.kpiBoxService.setControlVoice(kpiToSelect);
        },
      });
  }

  private getNextKpi(kpis: Kpi[], selectedKPIId: number, offset: number): Kpi {
    // KPIs with an existing dashboard
    const selectableKPIs = kpis.filter(kpi => kpi.mainDashboard);
    const currentKpiIndex = selectableKPIs.findIndex(
      kpi => kpi.kpiId === selectedKPIId
    );

    return selectableKPIs[
      (selectableKPIs.length + currentKpiIndex + offset) % selectableKPIs.length
    ];
  }
}
